import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import clientService from '../services/clientService';

interface Client {
  id: string;
  name: string;
}

interface ClientFormProps {
  open: boolean;
  onClose: (clientModified: boolean) => void;
  client: Client | null;
}

const ClientForm: React.FC<ClientFormProps> = ({ open, onClose, client }) => {
  const [nit, setNit] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (client) {
      // Note: Editing logic might need adjustment based on the new naming scheme
      // For now, we clear the form for new clients
      setNit('');
      setCompanyName('');
    } else {
      setNit('');
      setCompanyName('');
    }
  }, [client, open]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      if (client) {
        // Update logic would need to be implemented based on requirements
        // For example, clientService.updateClient(client.id, { nit, company_name: companyName });
        console.warn("Update functionality not implemented for nit and company name");
      } else {
        await clientService.createClient({ nit, company_name: companyName });
      }
      setSubmitting(false);
      onClose(true);
    } catch (err: any) {
      setError(err.response?.data?.detail || `Error al ${client ? 'actualizar' : 'crear'} el cliente.`);
      console.error(err);
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      onClose(false);
      setNit('');
      setCompanyName('');
      setError(null);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{client ? 'Editar Cliente' : 'Añadir Nuevo Cliente'}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <TextField
            autoFocus
            margin="dense"
            label="NIT"
            type="text"
            fullWidth
            variant="outlined"
            value={nit}
            onChange={(e) => setNit(e.target.value)}
            required
          />
          <TextField
            margin="dense"
            label="Nombre de la Empresa"
            type="text"
            fullWidth
            variant="outlined"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            required
          />
        </DialogContent>
        <DialogActions sx={{ p: '16px 24px' }}>
          <Button onClick={handleClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button type="submit" variant="contained" disabled={submitting}>
            {submitting ? <CircularProgress size={24} /> : (client ? 'Guardar Cambios' : 'Añadir Cliente')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ClientForm;